<script lang="ts">
import { defineComponent, PropType } from 'vue';
import { getMapFiltersData, getAuthors } from '@/api/endpoints/map/map.endpoints';
import MapFilters from '@/components/map/MapFilters.vue';
import { MapParams, AuthorsResData, MapFilterResData } from '@/api/endpoints/map/map.types';
import { RoutePath } from '@/enums/RoutePath.eunm';
import {
	getFeatureName,
	getFilterParams,
	saveFilterParams,
	generateColorGrid,
  getDataTypeLabel,
  getSelectedVariableLabel,
  getSelectedBasePeriodLabel,
  getSelectedGWLLabel,
  getSelectedScenarioLabel,
  getSelectedSeasonLabel,
  getColorForValue,
} from '@/utils/map.utils';
import { capitalizeWord, convertSnakeToCamelCase, falsy, t } from '@/utils/utils';
import { INIT_MAP_PARAMS } from '@/utils/constants.utils';
import MapRenderer from '@/components/map/MapRenderer.vue';
import { MapFiltersReqModel } from '@/models/MapFiltersReq.model';
import { FeatureSelect } from '@/models/FeatureSelect';
import { tourMap } from '@/tours/map.tour';
import Shepherd from 'shepherd.js';
import { tourSetToHide, tourGetToHide } from '@/utils/tour.utils';
import { MapFiltersComponent } from '@/models/MapFiltersComponent.model';
import { MapRendererComponent } from '@/models/MapRendererComponent.model';
import { Feature } from 'ol';
import { MapFiltersState } from '@/models/MapFiltersState.model';
import { MapInfoItem } from '@/models/MapInfoItem.model';
import { LonLat } from '@/models/LonLat.model';
import { toLonLat } from 'ol/proj';
import { getCenter } from 'ol/extent';
import { TourStepId } from '@/enums/TourStepId.enum';
import { useMeta } from 'vue-meta';
import { ColorRange } from '@/models/ColorRange.model';
import { Filters } from '@/models/Filters.model';
import EmptyState from '@/components/shared/EmptyState.vue';
import { AxiosError } from 'axios';

/**
 *
 * @version 1.0.0
 * @since
 */
export default defineComponent({
	name: 'Map',
	components: { MapRenderer, MapFilters, EmptyState },
	props: {
		params: {
			required: false,
			type: Object as PropType<MapParams>,
			default: () => ({}),
		},
		shadowRoot: {
			required: true,
			type: [ShadowRoot, null],
		},
	},
	setup() {
		useMeta({
			title: t('nav.map'),
		})
	},
	data() {
		return {
			tour: tourMap,
			data: null as MapFilterResData | null,
			filters: {} as Filters,
			areaName: '',
			mapParams: null as MapParams | null,
			term: '',
			searchResults: [] as any[],
			toShowResults: true,
			filterState: {} as MapFiltersState,
			lat: null as number | null,
			lon: null as number | null,
			authors: [] as AuthorsResData[],
			isLoadingAuthors: false,
      has30arcsec: false,
      hasNoData: false,
      errorMessage: null as string | null,
		};
	},
	computed: {
		mapFilters(): MapFiltersComponent {
			return this.$refs.mapFilters as MapFiltersComponent;
		},
		mapInfoTitle(): string {
			if (falsy(this.filterState)) {
				return '';
			}
			return `${t(`filters.variable.${this.filterState.variablesSelected}Title`)} (${t(`unit.${this.data.unit}`)})`;
		},
    variableDescription(): string {
      if (falsy(this.filterState)) {
        return '';
      }
      return t(`filters.variable.${this.filterState.variablesSelected}Description`);
    },
		gridValues(): string[] {
			if (!this.data?.valueGrid) return [];
			const colorGrid = generateColorGrid(this.data.valueGrid, this.data.colorRange);
			return Array.isArray(colorGrid) ? colorGrid : [];
		},
    defaultColor(): string | null {
      if (!this.data?.colorRange) return '';
      const colour = getColorForValue(0, this.data.colorRange);
      console.log('colour-defaultColor', colour);
      return colour;
    },
    hasValues(): number[] {
      if (!this.data?.valueGrid) return [];
      return Object.keys(this.data.valueGrid).map(Number);
    },

    mapInfoList() {
      const {
        basePeriodSelected,
				scenarioSelected,
				seasonSelected,
        gwlSelected,
        authorSelectedLabel,
        dataTypeSelected,
        variablesSelected,
			} = this.filterState;
      return [
      new MapInfoItem({
					title: t('filters.author.title'),
					text: authorSelectedLabel,
				}),
        new MapInfoItem({
					title: t('filters.dataType.mapInfoTitle'),
					text: getDataTypeLabel(dataTypeSelected),
				}),
				new MapInfoItem({
					title: t('filters.variable.title'),
					text: getSelectedVariableLabel(variablesSelected),
				}),
				basePeriodSelected &&
					new MapInfoItem({
						title: t('filters.period.title'),
						text: getSelectedBasePeriodLabel(basePeriodSelected),
					}),
        gwlSelected && new MapInfoItem({
            title: t('filters.gwl.title'),
            text: getSelectedGWLLabel(gwlSelected),
				  }),
				scenarioSelected &&
					new MapInfoItem({
						title: t('filters.scenario.title'),
						text: getSelectedScenarioLabel(scenarioSelected),
					}),
				new MapInfoItem({
					title: t('filters.season.title'),
					text: getSelectedSeasonLabel(seasonSelected),
				})
			].filter(Boolean);
    }
	},

	async created() {
		this.mapParams = !falsy(this.params)
			? (this.params as MapParams)
			: getFilterParams() || INIT_MAP_PARAMS;

		// Fetch authors and initialize map
		await this.fetchAuthors();
	},
	mounted() {
		this.$nextTick(() => {
			// this.initTour();
			if (!tourGetToHide()) {
				this.startTour();
			}
		});
		['complete', 'cancel'].forEach((event) =>
			(Shepherd as any).on(event, () => {
				tourSetToHide(true);
			})
		);
	},
	methods: {
    startTour(): void {
			this.mapFilters.closeFilterDrawer();
			this.unselectRegion();
			this.closePopup();
			this.tour.start();
		},
		showTourStep(id: TourStepId): void {
			this.tour.start();
			this.tour.show(id);
		},
		async fetchAuthors(): Promise<void> {
			try {
				this.isLoadingAuthors = true;
				const response = await getAuthors();
				this.authors = response.data.data;

				// If we have authors and no author is selected, select the first one
				if (this.authors.length > 0 && !this.mapParams?.author) {
					const firstAuthor = this.authors[0];
					// Send only author for author selection
					const requestParams = new MapFiltersReqModel({
						author: firstAuthor.slug,
					});
					await this.updateData(requestParams);
				} else if (this.mapParams?.author) {
					// If we already have an author, just update the data with current params
					await this.updateData(new MapFiltersReqModel(this.mapParams));
				}
			} catch (error) {
				console.error('Error fetching authors:', error);
			} finally {
				this.isLoadingAuthors = false;
			}
		},
		async updateData(obj: MapFiltersReqModel): Promise<void> {
			let requestParams = obj;
			if (obj.author && obj.author !== '' && (!this.mapParams || this.mapParams.author !== obj.author)) {
				// When author changes, send only author
				requestParams = new MapFiltersReqModel({
					author: obj.author
				});
			}

      try {
        const {
          data: {
            data,
            meta: { filters, has_30arcsec },
          },
        } = await getMapFiltersData(requestParams);
        this.hasNoData = false;

        this.mapParams = { ...requestParams };
        this.saveMapParams(this.mapParams);

        this.data = data;
        this.filters = filters;
        this.has30arcsec = has_30arcsec;
        this.unselectRegion();
        this.closePopup();
      } catch (error) {
        this.hasNoData = true;
        const errorStatus = (error as AxiosError).response?.status;
        this.errorMessage = errorStatus === 404 ? t('message.emptyState.noData.message') : t('message.emptyState.unknownAuthor.message');
        this.mapParams = { ...requestParams };
        this.saveMapParams(this.mapParams);
        this.filters = {
          author: { selected: requestParams.author }
        };
      }
		},
		saveMapParams(obj: MapParams): void {
			const { href } = this.$router.resolve({ path: RoutePath.MAP, query: { ...obj } });
			window.history.pushState({}, null, href);
			saveFilterParams(obj);
		},
		unselectRegion(): void {
			const mapRenderer = this.$refs.mapRenderer as MapRendererComponent;
			if (mapRenderer) {
				mapRenderer.unselectRegion();
			}
		},
		closePopup(): void {
			const mapRenderer = this.$refs.mapRenderer as MapRendererComponent;
			if (mapRenderer) {
				mapRenderer.closePopup();
			}
		},
		convertNameForDisplay(str: string): string {
			if (!str) {
				return '';
			}

			return convertSnakeToCamelCase({
				str,
				separator: ' ',
			})
				.split(' ')
				.map((word) => capitalizeWord(word))
				.join(' ');
		},
		searchPoint(lonLat: LonLat) {
			const mapRenderer = this.$refs.mapRenderer as MapRendererComponent;

			if (!mapRenderer) {
				return;
			}

			this.lon = lonLat.lon;
			this.lat = lonLat.lat;
			const feat = mapRenderer.searchPoint(lonLat);
			this.closePopup();
			const selectedList = mapRenderer.select.getFeatures();
			selectedList.push(feat);
			mapRenderer.selectRegion(feat);
		},
		search(term: string): void {
			const mapRenderer = this.$refs.mapRenderer as MapRendererComponent;

			if (!mapRenderer) {
				return;
			}

			this.term = term;
			const features = mapRenderer.search(term);
			this.searchResults = features.map(
				(feature) =>
					new FeatureSelect({
						key: feature.get('shortName'),
						name: this.convertNameForDisplay(getFeatureName(feature)),
						feature,
					})
			);
			this.toShowResults = true;
		},
		selectSearch(item: FeatureSelect): void {
			const mapRenderer = this.$refs.mapRenderer as MapRendererComponent;
			if (!mapRenderer) {
				return;
			}

			this.closePopup();
			const selectedList = mapRenderer.select.getFeatures();
			const { feature } = item;
			selectedList.push(feature);
			mapRenderer.selectRegion(feature);
		},
		resetSearchState(): void {
			this.term = '';
			this.areaName = '';
			this.searchResults = [];
		},
		updateTerm(feature: Feature): void {
			this.term = this.convertNameForDisplay(getFeatureName(feature)) ?? '';
			this.areaName = this.term;
			this.toShowResults = false;
		},
		updatePoint(feature: Feature): void {
			const center = getCenter(feature.getGeometry().getExtent());
			[this.lon, this.lat] = toLonLat(center);
		},
		clearLonLat(): void {
			this.lon = null;
			this.lat = null;
		},
		generateColorGrid(valueGrid: Record<number, number>, colorRange: Array<ColorRange>): string[] {
			return generateColorGrid(valueGrid, colorRange);
		},
		updateMapInfo(filterState: MapFiltersState): void {
			this.filterState = filterState;
		},
		onCloseFilterDrawer(): void {
			this.mapFilters.closeFilterDrawer();
		},

	},
});
</script>
<template>
	<div class="map-container">
		<div class="map width-100">
			<div class="map__side-container">
				<map-filters
					ref="mapFilters"
					:filters="filters"
					:term="term"
					:search-results="searchResults"
					:to-show-results="toShowResults"
					:lon="lon"
					:lat="lat"
					:authors="authors"
					:is-loading-authors="isLoadingAuthors"
					:current-author="mapParams?.author"
					@select="updateData"
					@search="search"
					@select-search="selectSearch"
					@unselect-search="unselectRegion"
					@search-point="searchPoint"
					@start-tour="startTour"
					@change-state="updateMapInfo"
					@show-tour-step="showTourStep"
				/>
			</div>
			<div class="flex-center width-100">
				<map-renderer
					v-if="data && !hasNoData"
					ref="mapRenderer"
					:data="data"
					:filters="filters"
					:file-id="data?.id"
					:grid-values="gridValues"
					:has-values="hasValues"
					:map-info-list="mapInfoList"
					:map-info-title="mapInfoTitle"
					:variable-description="variableDescription"
					:has-30arcsec="has30arcsec"
					:shadow-root="shadowRoot"
					:default-color="defaultColor"
					@select-region="
						updateTerm($event);
						updatePoint($event);
						onCloseFilterDrawer();
					"
					@unselect-region="resetSearchState"
				/>
				<empty-state v-else-if="hasNoData" :error-message="errorMessage" />
			</div>
		</div>
	</div>
</template>
<style lang="scss" scoped>
// If you are changing this width, also change c-drawer offset in MapFilters.vue
$c-side-width: 360px;

.map {
	position: relative;
	display: flex;
	height: 100%;
}

.map-container {
	height: calc(100vh - 140px) ;
	width: 100%;
}

.map__side-container {
	overflow: auto;
	min-width: $c-side-width;
	max-width: $c-side-width;
	background-color: #E3F5FA;
	box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
}
</style>
